import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { AREAS } from "@/lib/constants";
import { Plus, Play, Trash2, Radar, CalendarClock, Power, PowerOff, Sparkles, Brain } from "lucide-react";
import ModuleHelp from "@/components/help/ModuleHelp";

const blank = {
  nome_job: "",
  palavras_chave: "",
  fontes_consultadas: ["Crossref", "OpenAlex", "Semantic Scholar"],
  idiomas: ["pt", "en"],
  paises: ["Brasil"],
  periodo_dias: 30,
  max_resultados: 25,
  grau_relevancia_min: 60,
  area_tematica: "GLO",
  periodicidade_dias: 7,
  auto_enrich_ai: true,
};

const SOURCES = ["Crossref", "OpenAlex", "Semantic Scholar"];
const LANGS = ["pt", "en", "es", "fr"];

export default function Coleta() {
  const [jobs, setJobs] = useState([]);
  const [scheduler, setScheduler] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(blank);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");

  const load = async () => {
    const { data } = await api.get("/search-jobs");
    setJobs(data);
    try {
      const { data: status } = await api.get("/search-jobs/scheduler/status");
      setScheduler(status);
    } catch (e) { console.warn("Scheduler status indisponível:", e); }
  };
  useEffect(() => { load(); }, []);

  const toggleAtivo = async (id, current) => {
    setErr(""); setInfo("");
    try {
      await api.patch(`/search-jobs/${id}`, { ativo: !current });
      setInfo(`Job ${!current ? "ativado e re-agendado" : "desativado e removido do scheduler"}.`);
      await load();
    } catch (e) { setErr(formatErr(e)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setInfo("");
    try {
      await api.post("/search-jobs", {
        ...form,
        palavras_chave: form.palavras_chave.split(",").map((s) => s.trim()).filter(Boolean),
      });
      setForm(blank); setShowForm(false);
      await load();
      setInfo("Job criado.");
    } catch (e) { setErr(formatErr(e)); }
  };

  const run = async (id) => {
    setErr(""); setInfo("");
    try {
      const { data } = await api.post(`/search-jobs/${id}/run`);
      const parts = [
        `${data.count} sementes inseridas`,
        data.skipped_duplicates != null ? `${data.skipped_duplicates} ignoradas (duplicadas)` : null,
        data.total_found != null ? `${data.total_found} encontradas nas fontes` : null,
        data.auto_enrich_ai && data.enrichment_scheduled ? `${data.enrichment_scheduled} em enriquecimento IA (background)` : null,
      ].filter(Boolean).join(" · ");
      setInfo(`Execução concluída — query: "${data.query}" · ${parts}`);
      await load();
    } catch (e) { setErr(formatErr(e)); }
  };

  const enrichPending = async () => {
    setErr(""); setInfo("");
    try {
      const { data } = await api.post("/search-jobs/enrich-pending");
      setInfo(data.message);
    } catch (e) { setErr(formatErr(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir job?")) return;
    await api.delete(`/search-jobs/${id}`);
    await load();
  };

  const toggleArr = (key, val) => {
    const cur = form[key];
    setForm({ ...form, [key]: cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val] });
  };

  return (
    <div className="space-y-4" data-testid="coleta-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-mil text-[var(--gold)]">Coleta Automática</div>
          <h1 className="font-display text-3xl font-bold mt-1 flex items-center gap-3">Jobs Semanais de Busca <ModuleHelp module="coleta" /></h1>
          <p className="text-[var(--text-muted)] text-sm mt-2 max-w-2xl">
            Configure buscas periódicas reais em <strong>Crossref</strong>, <strong>OpenAlex</strong> e <strong>Semantic Scholar</strong>.
            Resultados entram como <span className="text-[var(--warn)] font-mono">PENDENTES</span> na fila de Revisão Humana, com dedup automático por DOI.
            <span className="ml-2 badge badge-success">Coleta Real · API ao vivo</span>
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={enrichPending} className="btn-ghost flex items-center gap-2" data-testid="coleta-enrich-pending">
            <Brain size={14} /> Enriquecer pendentes
          </button>
          <button onClick={() => setShowForm(!showForm)} className="btn-gold flex items-center gap-2" data-testid="coleta-new-button">
            <Plus size={14} /> Novo Job
          </button>
        </div>
      </div>

      {err && <div className="text-sm text-[var(--danger)] border border-[var(--danger)]/40 bg-[var(--danger)]/10 p-3 rounded">{err}</div>}
      {info && <div className="text-sm text-[var(--success)] border border-[var(--success)]/40 bg-[var(--success)]/10 p-3 rounded">{info}</div>}

      {/* Scheduler status */}
      {scheduler && (
        <div className="card-mil p-4 flex flex-wrap items-center gap-4" data-testid="scheduler-status">
          <div className="flex items-center gap-2">
            <CalendarClock size={16} className={scheduler.running ? "text-[var(--success)]" : "text-[var(--text-muted)]"} />
            <div>
              <div className="label-mil text-[10px]">Scheduler APScheduler</div>
              <div className="font-mono text-xs">
                {scheduler.enabled ? (scheduler.running ? "ATIVO · executando jobs ativos no intervalo configurado" : "PARADO") : "DESABILITADO via env"}
              </div>
            </div>
          </div>
          <div className="text-[10px] font-mono text-[var(--text-muted)] uppercase tracking-widest ml-auto">
            {scheduler.count ?? 0} JOBS AGENDADOS
          </div>
        </div>
      )}

      {showForm && (
        <form onSubmit={submit} className="card-mil p-5 grid grid-cols-1 md:grid-cols-2 gap-4 gold-accent-top" data-testid="coleta-form">
          <div className="md:col-span-2">
            <label className="label-mil block mb-1">Nome do job</label>
            <input className="input-mil" required value={form.nome_job} onChange={(e) => setForm({ ...form, nome_job: e.target.value })} data-testid="coleta-nome" />
          </div>
          <div className="md:col-span-2">
            <label className="label-mil block mb-1">Palavras-chave (vírgula)</label>
            <input className="input-mil" required value={form.palavras_chave} onChange={(e) => setForm({ ...form, palavras_chave: e.target.value })} placeholder="GLO, fronteiras, drones submarinos" data-testid="coleta-kw" />
          </div>
          <div>
            <label className="label-mil block mb-2">Fontes</label>
            <div className="flex flex-wrap gap-1.5">
              {SOURCES.map((s) => (
                <button type="button" key={s} onClick={() => toggleArr("fontes_consultadas", s)}
                        className={`text-[10px] px-2 py-1 border rounded font-mono ${form.fontes_consultadas.includes(s) ? "border-[var(--gold)] text-[var(--gold)] bg-[var(--gold)]/10" : "border-[var(--border)] text-[var(--text-muted)]"}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="label-mil block mb-2">Idiomas</label>
            <div className="flex flex-wrap gap-1.5">
              {LANGS.map((l) => (
                <button type="button" key={l} onClick={() => toggleArr("idiomas", l)}
                        className={`text-[10px] px-2 py-1 border rounded font-mono ${form.idiomas.includes(l) ? "border-[var(--info)] text-[var(--info)] bg-[var(--info)]/10" : "border-[var(--border)] text-[var(--text-muted)]"}`}>
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="label-mil block mb-1">Área temática</label>
            <select className="input-mil" value={form.area_tematica} onChange={(e) => setForm({ ...form, area_tematica: e.target.value })}>
              {AREAS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-3 gap-2 md:col-span-2">
            <div>
              <label className="label-mil block mb-1">Período (dias)</label>
              <input type="number" className="input-mil" value={form.periodo_dias} onChange={(e) => setForm({ ...form, periodo_dias: +e.target.value })} />
            </div>
            <div>
              <label className="label-mil block mb-1">Máx. resultados</label>
              <input type="number" className="input-mil" value={form.max_resultados} onChange={(e) => setForm({ ...form, max_resultados: +e.target.value })} />
            </div>
            <div>
              <label className="label-mil block mb-1">Periodicidade (dias)</label>
              <input type="number" className="input-mil" value={form.periodicidade_dias} onChange={(e) => setForm({ ...form, periodicidade_dias: +e.target.value })} />
            </div>
          </div>
          <div className="md:col-span-2 flex items-center justify-between flex-wrap gap-3 border border-[var(--gold)]/30 bg-[var(--gold)]/5 p-3 rounded">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.auto_enrich_ai}
                onChange={(e) => setForm({ ...form, auto_enrich_ai: e.target.checked })}
                className="mt-1 accent-[var(--gold)]"
                data-testid="coleta-auto-enrich"
              />
              <span>
                <div className="flex items-center gap-1.5 font-semibold text-sm">
                  <Sparkles size={12} className="text-[var(--gold)]" /> Enriquecimento automático via Gemini
                </div>
                <div className="text-[10px] text-[var(--text-muted)] mt-0.5">
                  Cada nova semente coletada é classificada (Classificação, PESTEL, Áreas, Palavras-chave, Confiança IA) em background — mantém status PENDENTE para revisão humana.
                </div>
              </span>
            </label>
          </div>
          <div className="md:col-span-2 flex gap-2 justify-end">
            <button type="button" className="btn-ghost" onClick={() => setShowForm(false)}>Cancelar</button>
            <button type="submit" className="btn-gold" data-testid="coleta-submit">Criar Job</button>
          </div>
        </form>
      )}

      <div className="card-mil overflow-hidden">
        <table className="w-full text-sm" data-testid="coleta-table">
          <thead>
            <tr>
              {["Job", "Palavras-chave", "Fontes", "Área", "Próx. exec.", "Encontrados", ""].map((h) => (
                <th key={h} className="px-4 py-3 label-mil border-b border-[var(--border)] text-left">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {jobs.map((j) => (
              <tr key={j.job_id} className="border-b border-[var(--border)]/50 hover:bg-[var(--surface-2)]">
                <td className="px-4 py-3 font-display font-semibold">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Radar size={14} className={j.ativo === false ? "text-[var(--text-muted)]" : "text-[var(--gold)]"} />
                    <span className={j.ativo === false ? "text-[var(--text-muted)] line-through" : ""}>{j.nome_job}</span>
                    {j.ativo === false && <span className="badge badge-muted text-[9px]">INATIVO</span>}
                    {j.auto_enrich_ai !== false && (
                      <span className="badge badge-gold text-[9px] flex items-center gap-0.5" title="Enriquecimento Gemini ativo">
                        <Sparkles size={9} /> IA
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-xs font-mono text-[var(--text-muted)] max-w-xs truncate">{(j.palavras_chave || []).join(", ")}</td>
                <td className="px-4 py-3 text-xs">
                  <div className="flex flex-wrap gap-1">
                    {(j.fontes_consultadas || []).slice(0, 3).map((s) => <span key={s} className="badge badge-info">{s}</span>)}
                  </div>
                </td>
                <td className="px-4 py-3 text-xs">{j.area_tematica || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs">
                  {j.proxima_execucao ? new Date(j.proxima_execucao).toLocaleDateString("pt-BR") : "—"}
                  <div className="text-[9px] text-[var(--text-muted)]">a cada {j.periodicidade_dias}d</div>
                </td>
                <td className="px-4 py-3"><span className="badge badge-gold">{j.resultados_encontrados || 0}</span></td>
                <td className="px-4 py-3 text-right">
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => toggleAtivo(j.job_id, j.ativo !== false)}
                            className="btn-ghost text-xs flex items-center gap-1"
                            data-testid={`toggle-${j.job_id}`}
                            title={j.ativo === false ? "Ativar e re-agendar" : "Desativar (parar scheduler para este job)"}>
                      {j.ativo === false ? <Power size={12} /> : <PowerOff size={12} />}
                      {j.ativo === false ? "Ativar" : "Desativar"}
                    </button>
                    <button onClick={() => run(j.job_id)} className="btn-ghost text-xs flex items-center gap-1" data-testid={`run-${j.job_id}`}>
                      <Play size={12} /> Executar
                    </button>
                    <button onClick={() => remove(j.job_id)} className="btn-ghost text-xs text-[var(--danger)]" data-testid={`del-${j.job_id}`}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {jobs.length === 0 && (
              <tr><td colSpan={7} className="text-center p-8 text-[var(--text-muted)] text-sm">Nenhum job configurado</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
